/** Internal type. DO NOT USE DIRECTLY. */
import { gql } from "@apollo/client"

import * as Types from "../../../shared/types/__generated__/graphql"
import {
  RoomChildrenFragmentDoc,
  RoomFragmentDoc
} from "../../rooms/graphql/fragments.generated"

export type Incremental<T> =
  | T
  | {
      [P in keyof T]?: P extends " $fragmentName" | "__typename" ? T[P] : never
    }

export type FloorFragment = {
  __typename: "Floor"
  id: string
  label?: string | null
  position: number
  createdAt: string
  updatedAt: string
  remark?: string | null
  hasClarificationRequest?: boolean | null
}

export type FloorChildrenFragment = {
  __typename: "Floor"
  children?: Array<{
    __typename: "Room"
    id: string
    label?: string | null
    number: string
    area?: number | null
    volume?: number | null
    utilization?: string | null
    height?: number | null
    personsNumber?: number | null
    airVolumePerArea?: number | null
    airVolumePerPerson?: number | null
    remark?: string | null
    sup?: number | null
    oda?: number | null
    eta?: number | null
    eha?: number | null
    tra?: number | null
    rca?: number | null
    ida?: number | null
    sec?: number | null
    soundPressureLevel?: number | null
    position: number
    createdAt: string
    updatedAt: string
    hasClarificationRequest?: boolean | null
    children?: Array<{
      __typename: "ProductPosition"
      id: string
      label?: string | null
      variantKey: string
      configurationKey?: string | null
      remark?: string | null
      quantity: number
      position: number
      displayPosition?: string | null
      isAlternative?: boolean | null
      price?: number | null
      priceGroup?: string | null
      hasClarificationRequest?: boolean | null
      references?: Array<{
        __typename: "ProductPositionInstallationReference"
        id: string
        title?: string | null
        permalink: string
        position: number
      }> | null
      addons?: Array<{
        __typename: "ProductPositionAddon"
        id: string
        variantKey: string
        parentProductType: string
      }> | null
      dimensioning?: {
        __typename: "ExternalDimensioningResult"
        transactionId: string
        environmentalVars?: Array<any> | null
        variantKeyData?: any
        dllVersion?: string | null
        language?: string | null
        technicalData?: Array<
          | {
              __typename: "ExternalDimensioningTechnicalDataDllVersion"
              dllVersion: string
            }
          | {
              __typename: "ExternalDimensioningTechnicalDataTable"
              header?: Array<string> | null
              content?: any
              summary?: string | null
              type?: string | null
            }
        > | null
        technicalInput?: Array<{
          __typename: "ExternalDimensioningTechnicalInput"
          name: string
          label?: string | null
          unit?: string | null
          value?: any
          userSelected: boolean
        }> | null
        supplementaryText?: Array<{
          __typename: "ExternalDimensioningSupplementaryText"
          text: string
        }> | null
        transferData?: {
          __typename: "ExternalDimensioningTransferData"
          origin?: string | null
        } | null
      } | null
    }> | null
  }> | null
}

export type FloorCompleteFragment = {
  __typename: "Floor"
  id: string
  label?: string | null
  position: number
  createdAt: string
  updatedAt: string
  remark?: string | null
  hasClarificationRequest?: boolean | null
  children?: Array<{
    __typename: "Room"
    id: string
    label?: string | null
    number: string
    area?: number | null
    volume?: number | null
    utilization?: string | null
    height?: number | null
    personsNumber?: number | null
    airVolumePerArea?: number | null
    airVolumePerPerson?: number | null
    remark?: string | null
    sup?: number | null
    oda?: number | null
    eta?: number | null
    eha?: number | null
    tra?: number | null
    rca?: number | null
    ida?: number | null
    sec?: number | null
    soundPressureLevel?: number | null
    position: number
    createdAt: string
    updatedAt: string
    hasClarificationRequest?: boolean | null
    children?: Array<{
      __typename: "ProductPosition"
      id: string
      label?: string | null
      variantKey: string
      configurationKey?: string | null
      remark?: string | null
      quantity: number
      position: number
      displayPosition?: string | null
      isAlternative?: boolean | null
      price?: number | null
      priceGroup?: string | null
      hasClarificationRequest?: boolean | null
      references?: Array<{
        __typename: "ProductPositionInstallationReference"
        id: string
        title?: string | null
        permalink: string
        position: number
      }> | null
      addons?: Array<{
        __typename: "ProductPositionAddon"
        id: string
        variantKey: string
        parentProductType: string
      }> | null
      dimensioning?: {
        __typename: "ExternalDimensioningResult"
        transactionId: string
        environmentalVars?: Array<any> | null
        variantKeyData?: any
        dllVersion?: string | null
        language?: string | null
        technicalData?: Array<
          | {
              __typename: "ExternalDimensioningTechnicalDataDllVersion"
              dllVersion: string
            }
          | {
              __typename: "ExternalDimensioningTechnicalDataTable"
              header?: Array<string> | null
              content?: any
              summary?: string | null
              type?: string | null
            }
        > | null
        technicalInput?: Array<{
          __typename: "ExternalDimensioningTechnicalInput"
          name: string
          label?: string | null
          unit?: string | null
          value?: any
          userSelected: boolean
        }> | null
        supplementaryText?: Array<{
          __typename: "ExternalDimensioningSupplementaryText"
          text: string
        }> | null
        transferData?: {
          __typename: "ExternalDimensioningTransferData"
          origin?: string | null
        } | null
      } | null
    }> | null
  }> | null
}

export type FloorParentFragment = {
  __typename: "Floor"
  parent?: { __typename: "Project"; id: string; title: string } | null
}

export const FloorFragmentDoc = gql`
  fragment Floor on Floor {
    id
    label
    position
    createdAt
    updatedAt
    remark
    hasClarificationRequest
  }
`
export const FloorChildrenFragmentDoc = gql`
  fragment FloorChildren on Floor {
    children {
      ... on Room {
        ...Room
        ...RoomChildren
      }
    }
  }
  ${RoomFragmentDoc}
  ${RoomChildrenFragmentDoc}
`
export const FloorCompleteFragmentDoc = gql`
  fragment FloorComplete on Floor {
    ...Floor
    ...FloorChildren
  }
  ${FloorFragmentDoc}
  ${FloorChildrenFragmentDoc}
`
export const FloorParentFragmentDoc = gql`
  fragment FloorParent on Floor {
    parent {
      ... on Project {
        id
        title
      }
    }
  }
`
